import { getDefaultStore } from 'jotai';
import {
  authTokensAtom,
  authUserAtom,
  authErrorAtom,
  authService,
  KNOWLEDGE_API_BASE_URL,
} from '@consentropy/shared';

const store = getDefaultStore();
const TOKEN_REFRESH_SKEW_MS = 30_000;

// Ingestion is enabled by default. Set VITE_KNOWLEDGE_INGEST_DISABLED=true to
// stop every ingestion path at this shared choke point.
const KNOWLEDGE_INGEST_DISABLED = import.meta.env.VITE_KNOWLEDGE_INGEST_DISABLED === 'true';

function handleUnauthorized(response: Response): void {
  if (response.status === 401) {
    store.set(authTokensAtom, null);
    store.set(authUserAtom, null);
    store.set(authErrorAtom, 'Session expired. Please sign in again.');
  }
}

async function getRequestTokens() {
  const currentTokens = store.get(authTokensAtom);
  if (!currentTokens) return null;

  if (currentTokens.expiresAt > Date.now() + TOKEN_REFRESH_SKEW_MS) {
    return currentTokens;
  }

  const refreshedTokens = await authService.getCurrentSession();
  if (!refreshedTokens) {
    store.set(authTokensAtom, null);
    store.set(authUserAtom, null);
    return null;
  }

  store.set(authTokensAtom, refreshedTokens);
  store.set(authUserAtom, authService.extractUserFromIdToken(refreshedTokens.idToken));
  return refreshedTokens;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const tokens = await getRequestTokens();
  return tokens?.idToken ? { Authorization: `Bearer ${tokens.idToken}` } : {};
}

export interface KnowledgeDocumentSummary {
  path: string;
  frontmatter: Record<string, unknown>;
}

export interface KnowledgeSearchResult {
  path: string;
  line: number;
  matchLine: string;
  context: string;
}

export interface KnowledgeQueryContext {
  tags?: string[];
}

export interface KnowledgeQueryInput {
  projectId: string;
  question: string;
  context?: KnowledgeQueryContext;
  job?: KnowledgeJobOptions;
}

export interface KnowledgeQueryResponse {
  answer: string;
  sources: string[];
  toolCalls: number;
  durationMs: number;
}

type KnowledgeJobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

interface KnowledgeJob<TResult> {
  jobId: string;
  status: KnowledgeJobStatus;
  result: TResult | null;
  error?: { message?: string } | string | null;
}

export interface KnowledgeJobOptions {
  /** Aborts submission and polling. The durable job keeps running server-side. */
  signal?: AbortSignal;
  /** Overall polling deadline. Defaults to 2 minutes for queries and 10 minutes for ingest. */
  timeoutMs?: number;
  pollIntervalMs?: number;
  /** Max consecutive transient failures tolerated (per request) before giving up. */
  maxTransientRetries?: number;
  retryBaseDelayMs?: number;
  /** Reuse to make a retried submission resolve to the same job instead of duplicating work. */
  idempotencyKey?: string;
  /** Called once the job ID is known, so callers can resume with resumeKnowledgeJob. */
  onJobCreated?: (jobId: string) => void;
}

const JOB_POLL_INTERVAL_MS = 5_000;
const QUERY_JOB_TIMEOUT_MS = 2 * 60_000;
const INGEST_JOB_TIMEOUT_MS = 10 * 60_000;
const RULEBOOK_COMPILE_TIMEOUT_MS = 30 * 60_000;
const JOB_MAX_TRANSIENT_RETRIES = 5;
const JOB_RETRY_BASE_DELAY_MS = 1_000;
const JOB_RETRY_MAX_DELAY_MS = 15_000;

export class KnowledgeApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'KnowledgeApiError';
    this.status = status;
  }
}

/** The job may still complete; resume with resumeKnowledgeJob(projectId, jobId). */
export class KnowledgeJobTimeoutError extends Error {
  readonly jobId: string;

  constructor(jobId: string, timeoutMs: number) {
    super(`Knowledge job ${jobId} did not finish within ${Math.round(timeoutMs / 1000)}s; it may still complete.`);
    this.name = 'KnowledgeJobTimeoutError';
    this.jobId = jobId;
  }
}

/** Polling was cancelled by the caller; the job itself is not cancelled. jobId is null if aborted before submission. */
export class KnowledgeJobAbortedError extends Error {
  readonly jobId: string | null;

  constructor(jobId: string | null) {
    super('Knowledge job polling was cancelled');
    this.name = 'KnowledgeJobAbortedError';
    this.jobId = jobId;
  }
}

export type KnowledgeQuerySessionStatus = 'processing' | 'awaiting_input' | 'ready' | 'closed' | 'expired';
export type KnowledgeQualificationType = 'text' | 'single_select' | 'multi_select';

export interface KnowledgeQualificationOption {
  value: string;
  label: string;
}

export interface KnowledgeQualificationPrompt {
  promptId: string;
  prompt: string;
  inputType: KnowledgeQualificationType;
  options?: KnowledgeQualificationOption[];
  required: boolean;
}

export interface KnowledgeQuerySessionTurn {
  turnId: string;
  role: 'user' | 'assistant';
  kind: 'question' | 'follow_up' | 'qualification' | 'answer';
  content: string;
  createdAt: string;
  promptId?: string;
  qualification?: KnowledgeQualificationPrompt;
  sources?: string[];
  webSources?: string[];
  toolCalls?: number;
  durationMs?: number;
}

export interface KnowledgeQuerySession {
  sessionId: string;
  projectId: string;
  status: KnowledgeQuerySessionStatus;
  mode: 'ai' | 'local_fallback';
  revision: number;
  turns: KnowledgeQuerySessionTurn[];
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  closedAt: string | null;
}

export async function listKnowledgeQuerySessions(projectId: string): Promise<KnowledgeQuerySession[]> {
  const project = requireProjectId(projectId);
  const response = await requestKnowledgeJson<{ sessions: KnowledgeQuerySession[] }>(
    `/projects/${project}/query-sessions`,
  );
  return response.sessions;
}

export interface StartKnowledgeQuerySessionInput {
  projectId: string;
  question: string;
  context?: KnowledgeQueryContext;
}

export interface ContinueKnowledgeQuerySessionInput {
  projectId: string;
  sessionId: string;
  expectedRevision: number;
  promptId?: string;
  answer?: string;
  selectedValue?: string;
  selectedValues?: string[];
  message?: string;
}

export interface RulebookRule {
  id: string; title: string; appliesWhen: string; requirement: string;
  evidenceRequired: string; exceptions: string; discipline: string;
  tier: 'core' | 'discipline'; verificationMethod: 'document-evidence' | 'human-required';
  sourceReferences: { path: string; version: string; section: string; quote: string }[];
  relationships?: { targetId: string; type: 'related-to' | 'requires' | 'adds-detail' | 'exception-to' | 'conflicts-with'; reason: string }[];
}
export interface ProjectRulebook {
  format?: 'prompts';
  promptEdition?: 2 | 3;
  baseVersionId?: string | null;
  changes?: { promptId: string; previousPromptIds: string[]; type: 'added' | 'revised' | 'unchanged' | 'conflicting'; reason: string }[];
  id: string; title: string; scope: string; revision: number; createdAt: string; createdBy: string;
  publishedAt: string | null; publishedBy: string | null;
  reviewWarningsAcknowledgedAt?: string | null; reviewWarningsAcknowledgedBy?: string | null;
  status: 'draft' | 'approved' | 'superseded';
  rules: RulebookRule[];
  sources: { path: string; version: string; hash: string; genre: string; healthIssues: string[] }[];
  questions: { id: string; description: string; affectedRuleIds: string[]; resolution: string }[];
  validationErrors: string[]; changedSources: string[];
}
export interface ProjectRulebookCatalog {
  activeId: string | null; canPublish: boolean; versions: ProjectRulebook[];
}
export function listProjectRulebooks(projectId: string): Promise<ProjectRulebookCatalog> {
  return requestKnowledgeJson(`/projects/${requireProjectId(projectId)}/rulebooks`);
}
export async function compileProjectRulebook(input: { projectId: string; title: string; sourcePaths: string[]; scope?: string }): Promise<ProjectRulebook> {
  const project = requireProjectId(input.projectId);
  const job = await requestKnowledgeJson<KnowledgeJob<{ output: ProjectRulebook }>>(`/projects/${project}/jobs`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ operation: 'generate', capabilityId: 'rulebook-compile',
      idempotencyKey: crypto.randomUUID(),
      params: { title: input.title, sourcePaths: input.sourcePaths, scope: input.scope ?? '' } }),
  });
  return (await waitForKnowledgeJob(project, job, {}, RULEBOOK_COMPILE_TIMEOUT_MS)).output;
}
export async function saveProjectRulebook(input: { projectId: string; book: ProjectRulebook }): Promise<ProjectRulebook> {
  const { book } = input;
  const result = await requestKnowledgeJson<{ rulebook: ProjectRulebook }>(
    `/projects/${requireProjectId(input.projectId)}/rulebooks/${encodeURIComponent(book.id)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expectedRevision: book.revision, title: book.title, scope: book.scope,
        rules: book.rules, questions: book.questions }),
    });
  return result.rulebook;
}
export async function publishProjectRulebook(input: { projectId: string; id: string; expectedRevision: number; expectedActiveId: string | null; reviewWarningsAcknowledged?: boolean }): Promise<ProjectRulebook> {
  const { projectId, ...body } = input;
  const { id, ...guard } = body;
  const result = await requestKnowledgeJson<{ rulebook: ProjectRulebook }>(
    `/projects/${requireProjectId(projectId)}/rulebooks/${encodeURIComponent(id)}/publish`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(guard),
    });
  return result.rulebook;
}

export interface KnowledgeIngestInput {
  projectId: string;
  file: File;
  conversationId?: string;
  sourceType?: string;
  sourceRef?: string;
  sourceOwner?: 'document-api';
  sourceId?: string;
  tags?: string[];
  supersedes?: string;
  job?: KnowledgeJobOptions;
}

export interface KnowledgeIngestResult {
  duplicate: boolean;
  path?: string;
  hash?: string;
  title?: string;
  existingPath?: string;
}

interface KnowledgeIngestUpload {
  url: string;
  fileUrl: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '');
}

function encodeKnowledgePath(path: string): string {
  return path
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
}

function buildKnowledgeUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizeBaseUrl(KNOWLEDGE_API_BASE_URL)}${normalizedPath}`;
}

function requireProjectId(projectId: string): string {
  if (!projectId.trim()) {
    throw new Error('projectId is required');
  }
  return encodeURIComponent(projectId);
}

function knowledgeUploadMimeType(file: File): string {
  if (file.type) return file.type;

  const fileName = file.name.toLowerCase();
  if (fileName.endsWith('.md') || fileName.endsWith('.markdown')) return 'text/markdown';
  if (fileName.endsWith('.txt')) return 'text/plain';
  return 'application/octet-stream';
}

function getErrorMessage(text: string): string {
  if (!text) return 'Unexpected knowledge API error';

  try {
    const parsed = JSON.parse(text) as { message?: string };
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message;
    }
  } catch {
    // Fall through to the raw response text.
  }

  return text;
}

async function requestKnowledgeJson<T>(path: string, init?: RequestInit): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(buildKnowledgeUrl(path), {
    cache: 'no-store',
    ...init,
    headers: {
      Accept: 'application/json',
      ...authHeaders,
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    handleUnauthorized(response);
    throw new KnowledgeApiError(getErrorMessage(await response.text()), response.status);
  }

  return (await response.json()) as T;
}

async function requestKnowledgeNoContent(path: string, init: RequestInit): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(buildKnowledgeUrl(path), {
    cache: 'no-store',
    ...init,
    headers: {
      Accept: 'application/json',
      ...authHeaders,
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    handleUnauthorized(response);
    throw new Error(getErrorMessage(await response.text()));
  }
}

async function requestKnowledgeText(path: string): Promise<string> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(buildKnowledgeUrl(path), {
    cache: 'no-store',
    headers: { Accept: 'text/markdown, text/plain, */*', ...authHeaders },
  });

  if (!response.ok) {
    handleUnauthorized(response);
    throw new Error(getErrorMessage(await response.text()));
  }

  return response.text();
}

function unwrapItems<T>(data: { items?: T[] } | T[]): T[] {
  return Array.isArray(data) ? data : (data.items ?? []);
}

export async function listKnowledgeDocuments(projectId: string): Promise<KnowledgeDocumentSummary[]> {
  const project = requireProjectId(projectId);
  const data = await requestKnowledgeJson<{ items?: KnowledgeDocumentSummary[] } | KnowledgeDocumentSummary[]>(
    `/projects/${project}/documents`,
  );
  return unwrapItems(data);
}

export async function searchKnowledge(projectId: string, query: string): Promise<KnowledgeSearchResult[]> {
  if (!query.trim()) return [];
  const project = requireProjectId(projectId);
  const data = await requestKnowledgeJson<{ items?: KnowledgeSearchResult[] } | KnowledgeSearchResult[]>(
    `/projects/${project}/documents/search?q=${encodeURIComponent(query)}`,
  );
  return unwrapItems(data);
}

export async function deleteKnowledgeDocument(projectId: string, path: string): Promise<void> {
  if (!path.trim()) throw new Error('Document path is required');
  const project = requireProjectId(projectId);
  await requestKnowledgeNoContent(`/projects/${project}/documents/${encodeKnowledgePath(path)}`, {
    method: 'DELETE',
  });
}

export async function readKnowledgeDocument(projectId: string, path: string): Promise<string> {
  if (!path.trim()) {
    throw new Error('Document path is required');
  }
  const project = requireProjectId(projectId);
  return requestKnowledgeText(`/projects/${project}/documents/${encodeKnowledgePath(path)}`);
}

export async function uploadKnowledgeDocument(input: KnowledgeIngestInput): Promise<KnowledgeIngestResult> {
  if (KNOWLEDGE_INGEST_DISABLED) {
    return { duplicate: false };
  }

  const project = requireProjectId(input.projectId);
  const mimeType = knowledgeUploadMimeType(input.file);
  const upload = await requestKnowledgeJson<KnowledgeIngestUpload>(`/projects/${project}/ingest-uploads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: input.file.name, mimeType, fileSize: input.file.size }),
  });
  const uploadResponse = await fetch(upload.url, {
    method: 'PUT',
    headers: { 'Content-Type': upload.mimeType },
    body: input.file,
  });
  if (!uploadResponse.ok) {
    throw new Error(`Knowledge file upload failed (${uploadResponse.status})`);
  }

  const options = input.job ?? {};
  const idempotencyKey = options.idempotencyKey ?? createIdempotencyKey('knowledge-ingest');
  const job = await submitKnowledgeJob<KnowledgeIngestResult>(project, options, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      idempotencyKey,
      operation: 'ingest',
      fileUrl: upload.fileUrl,
      fileName: upload.fileName,
      mimeType: upload.mimeType,
      conversationId: input.conversationId,
      sourceType: input.sourceType,
      sourceRef: input.sourceRef,
      sourceOwner: input.sourceOwner,
      sourceId: input.sourceId,
      tags: input.tags,
      supersedes: input.supersedes,
    }),
  });
  return waitForKnowledgeJob(project, job, options, INGEST_JOB_TIMEOUT_MS);
}

export async function queryKnowledge(input: KnowledgeQueryInput): Promise<KnowledgeQueryResponse> {
  const project = requireProjectId(input.projectId);
  const options = input.job ?? {};
  const idempotencyKey = options.idempotencyKey ?? createIdempotencyKey('knowledge-query');
  const job = await submitKnowledgeJob<KnowledgeQueryResponse>(project, options, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idempotencyKey, operation: 'query', question: input.question, context: input.context }),
  });
  return waitForKnowledgeJob(project, job, options, QUERY_JOB_TIMEOUT_MS);
}

export async function resumeKnowledgeJob<TResult = KnowledgeQueryResponse | KnowledgeIngestResult>(
  projectId: string,
  jobId: string,
  options: KnowledgeJobOptions = {},
  defaultTimeoutMs: number = QUERY_JOB_TIMEOUT_MS,
): Promise<TResult> {
  const project = requireProjectId(projectId);
  if (!jobId.trim()) throw new Error('jobId is required');
  const job = await withTransientRetry(
    () => requestKnowledgeJson<KnowledgeJob<TResult>>(
      `/projects/${project}/jobs/${encodeURIComponent(jobId)}`,
      { signal: options.signal },
    ),
    options,
    jobId,
  );
  return waitForKnowledgeJob(project, job, options, defaultTimeoutMs);
}

function jobErrorMessage(error: KnowledgeJob<unknown>['error']): string {
  if (typeof error === 'string') return error;
  return error?.message ?? 'Knowledge job failed';
}

function isTransientError(error: unknown): boolean {
  if (error instanceof KnowledgeApiError) return error.status === 429 || error.status >= 500;
  return error instanceof TypeError;
}

function throwIfAborted(signal: AbortSignal | undefined, jobId: string | null): void {
  if (signal?.aborted) throw new KnowledgeJobAbortedError(jobId);
}

function abortableDelay(ms: number, signal: AbortSignal | undefined, jobId: string | null): Promise<void> {
  throwIfAborted(signal, jobId);
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer);
      reject(new KnowledgeJobAbortedError(jobId));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

async function withTransientRetry<T>(
  operation: () => Promise<T>,
  options: KnowledgeJobOptions,
  jobId: string | null,
): Promise<T> {
  const maxRetries = options.maxTransientRetries ?? JOB_MAX_TRANSIENT_RETRIES;
  const baseDelay = options.retryBaseDelayMs ?? JOB_RETRY_BASE_DELAY_MS;
  for (let attempt = 0; ; attempt += 1) {
    throwIfAborted(options.signal, jobId);
    try {
      return await operation();
    } catch (error) {
      if (options.signal?.aborted) throw new KnowledgeJobAbortedError(jobId);
      if (!isTransientError(error) || attempt >= maxRetries) throw error;
      const backoff = Math.min(baseDelay * 2 ** attempt, JOB_RETRY_MAX_DELAY_MS);
      await abortableDelay(backoff / 2 + Math.random() * (backoff / 2), options.signal, jobId);
    }
  }
}

async function submitKnowledgeJob<TResult>(
  project: string,
  options: KnowledgeJobOptions,
  init: RequestInit,
): Promise<KnowledgeJob<TResult>> {
  // Safe to retry: the body carries an idempotencyKey, so the API resolves repeats to the same job.
  const job = await withTransientRetry(
    () => requestKnowledgeJson<KnowledgeJob<TResult>>(`/projects/${project}/jobs`, { ...init, signal: options.signal }),
    options,
    null,
  );
  options.onJobCreated?.(job.jobId);
  return job;
}

async function waitForKnowledgeJob<TResult>(
  project: string,
  initialJob: KnowledgeJob<TResult>,
  options: KnowledgeJobOptions,
  defaultTimeoutMs: number,
): Promise<TResult> {
  const timeoutMs = options.timeoutMs ?? defaultTimeoutMs;
  const pollIntervalMs = options.pollIntervalMs ?? JOB_POLL_INTERVAL_MS;
  const deadline = Date.now() + timeoutMs;
  let job = initialJob;
  const jobId = initialJob.jobId;

  while (job.status === 'queued' || job.status === 'running') {
    job = await withTransientRetry(
      () => requestKnowledgeJson<KnowledgeJob<TResult>>(
        `/projects/${project}/jobs/${encodeURIComponent(jobId)}`,
        { signal: options.signal },
      ),
      options,
      jobId,
    );
    if (job.status === 'queued' || job.status === 'running') {
      const remaining = deadline - Date.now();
      if (remaining <= 0) throw new KnowledgeJobTimeoutError(jobId, timeoutMs);
      await abortableDelay(Math.min(pollIntervalMs, remaining), options.signal, jobId);
    }
  }
  if (job.status === 'failed') throw new Error(jobErrorMessage(job.error));
  if (job.result === null) throw new Error('Knowledge job completed without a result');
  return job.result;
}

function createIdempotencyKey(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function startKnowledgeQuerySession(
  input: StartKnowledgeQuerySessionInput,
): Promise<KnowledgeQuerySession> {
  const project = requireProjectId(input.projectId);
  const response = await requestKnowledgeJson<{ session: KnowledgeQuerySession }>(`/projects/${project}/query-sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': createIdempotencyKey('knowledge-query-start'),
    },
    body: JSON.stringify({ question: input.question, context: input.context }),
  });
  return response.session;
}

export async function continueKnowledgeQuerySession(
  input: ContinueKnowledgeQuerySessionInput,
): Promise<KnowledgeQuerySession> {
  const project = requireProjectId(input.projectId);
  const response = await requestKnowledgeJson<{ session: KnowledgeQuerySession }>(
    `/projects/${project}/query-sessions/${encodeURIComponent(input.sessionId)}/turns`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': createIdempotencyKey('knowledge-query-turn'),
      },
      body: JSON.stringify({
        expectedRevision: input.expectedRevision,
        promptId: input.promptId,
        answer: input.answer,
        selectedValue: input.selectedValue,
        selectedValues: input.selectedValues,
        message: input.message,
      }),
    },
  );
  return response.session;
}

export async function getKnowledgeQuerySession(projectId: string, sessionId: string): Promise<KnowledgeQuerySession> {
  const project = requireProjectId(projectId);
  if (!sessionId.trim()) {
    throw new Error('sessionId is required');
  }
  const response = await requestKnowledgeJson<{ session: KnowledgeQuerySession }>(
    `/projects/${project}/query-sessions/${encodeURIComponent(sessionId)}`,
  );
  return response.session;
}

export async function closeKnowledgeQuerySession(input: {
  projectId: string;
  sessionId: string;
  expectedRevision: number;
}): Promise<void> {
  const project = requireProjectId(input.projectId);
  if (!input.sessionId.trim()) {
    throw new Error('sessionId is required');
  }
  await requestKnowledgeNoContent(
    `/projects/${project}/query-sessions/${encodeURIComponent(input.sessionId)}`,
    {
      method: 'DELETE',
      headers: { 'If-Match': String(input.expectedRevision) },
    },
  );
}

export async function deleteKnowledgeQuerySession(projectId: string, sessionId: string): Promise<void> {
  const project = requireProjectId(projectId);
  if (!sessionId.trim()) {
    throw new Error('sessionId is required');
  }
  await requestKnowledgeNoContent(
    `/projects/${project}/query-sessions/${encodeURIComponent(sessionId)}/permanent`,
    { method: 'DELETE' },
  );
}

export interface KnowledgeExportResponse {
  url: string;
  expiresInSeconds: number;
  snapshotHash: string;
  builtAt: string;
  fileCount: number;
  sizeBytes: number;
  rebuilt: boolean;
}

export async function getKnowledgeExport(projectId: string): Promise<KnowledgeExportResponse> {
  const project = requireProjectId(projectId);
  return requestKnowledgeJson<KnowledgeExportResponse>(`/projects/${project}/export`);
}
